import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getDocs, collection } from "firebase/firestore";
import { db } from "../firebase";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import MarkdownRenderer from "../components/MarkdownRenderer";
import { apps } from "../data/apps";
import LikeButton from "../components/LikeButton";
import CommentSection from "../components/CommentSection";
import { useAuth } from "../contexts/AuthContext";
import {
  trackBlogView,
  trackSocialShare,
  trackCopyLink,
  trackRelatedBlogClick,
  trackBlogReadTime,
} from "../utils/analytics";

// Helper function to generate URL slug from title
const generateSlug = (title) => {
  return (title || "")
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
    .substring(0, 60); // Limit length
};

// Animated background grid
const GridBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    <div
      className="absolute inset-0 opacity-[0.02]"
      style={{
        backgroundImage: `
          linear-gradient(rgba(168, 114, 14, 0.5) 1px, transparent 1px),
          linear-gradient(90deg, rgba(168, 114, 14, 0.5) 1px, transparent 1px)
        `,
        backgroundSize: "60px 60px",
      }}
    />
  </div>
);

// Floating orbs background
const FloatingOrbs = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    <motion.div
      className="absolute w-[300px] h-[300px] md:w-[600px] md:h-[600px] rounded-full opacity-30"
      style={{
        background: "rgba(232, 180, 76, 0.10)",
        left: "-20%",
        top: "10%",
      }}
      animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
      transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute w-[250px] h-[250px] md:w-[500px] md:h-[500px] rounded-full opacity-30"
      style={{
        background: "rgba(232, 180, 76, 0.08)",
        right: "-15%",
        bottom: "20%",
      }}
      animate={{ x: [0, -30, 0], y: [0, -50, 0] }}
      transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
);

// Category badge
const CategoryBadge = ({ category }) => {
  const styles = {
    AI: "bg-[#F1EEE5] text-[#A8720E] border-[#E1DCCE]",
    Technology: "bg-[#F1EEE5] text-[#A8720E] border-[#E1DCCE]",
    Tutorial: "bg-[#F1EEE5] text-[#A8720E] border-[#E1DCCE]",
    Update: "bg-[#F1EEE5] text-[#A8720E] border-[#E1DCCE]",
  };
  return (
    <span
      className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border ${
        styles[category] || styles.Technology
      }`}
    >
      {category}
    </span>
  );
};

// Related blog card
const RelatedBlogCard = ({ blog, currentBlogTitle, user }) => {
  const navigate = useNavigate();
  const slug = generateSlug(blog.title);

  const handleClick = () => {
    trackRelatedBlogClick(currentBlogTitle, blog.title, user);
    navigate(`/blog/${slug}`, { state: { blogId: blog.firestoreId } });
  };

  const extractExcerpt = (markdown) => {
    // Remove markdown syntax and get plain text
    const plainText = markdown
      .replace(/^#+ .+$/gm, "") // Remove headings
      .replace(/\*\*(.+?)\*\*/g, "$1") // Remove bold
      .replace(/\*(.+?)\*/g, "$1") // Remove italic
      .replace(/\[(.+?)\]\(.+?\)/g, "$1") // Remove links
      .replace(/`(.+?)`/g, "$1") // Remove inline code
      .replace(/^\s*[-*+]\s+/gm, "") // Remove list markers
      .trim();
    return plainText.substring(0, 100) + (plainText.length > 100 ? "..." : "");
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      onClick={handleClick}
      className="group relative cursor-pointer"
    >
      <div className="absolute -inset-[1px] bg-[#E8B44C]/15 rounded-xl opacity-0 group-hover:opacity-100 blur-sm transition-opacity" />
      <div className="relative bg-white shadow-[0_1px_2px_rgba(52,44,26,0.06),0_4px_14px_rgba(52,44,26,0.05)] rounded-xl border border-[#E1DCCE] group-hover:border-[#D9A32E] p-4 transition-all">
        <div className="flex items-center gap-2 mb-2">
          <CategoryBadge category={blog.category} />
          <span className="text-xs text-[#8A93A2] font-mono">
            {new Date(blog.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>
        <h4 className="text-sm font-semibold text-[#26313B] mb-2 line-clamp-2">
          {blog.title || "Untitled Post"}
        </h4>
        <p className="text-xs text-[#5B6B7C] line-clamp-2">
          {extractExcerpt(blog.text)}
        </p>
      </div>
    </motion.div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const fetchBlogAndRelated = async () => {
      try {
        const snapshot = await getDocs(collection(db, "Blogs"));
        const allBlogs = snapshot.docs.map((docItem) => ({
          firestoreId: docItem.id,
          title: docItem.data().blog_title || "",
          text: docItem.data().blog_contetnt,
          category: docItem.data().blog_category || "Uncategorized",
          createdAt: docItem.data().createdAt || Date.now(),
          likesCount: docItem.data().likesCount || 0,
          commentsCount: docItem.data().commentsCount || 0,
          appId: docItem.data().app_id || null,
        }));

        // Find the blog by slug
        const currentBlog = allBlogs.find(
          (b) => generateSlug(b.title) === slug
        );

        if (currentBlog) {
          setBlog(currentBlog);

          // Get 3 other blogs from the same category, or just recent ones
          const related = allBlogs
            .filter((b) => b.firestoreId !== currentBlog.firestoreId)
            .sort((a, b) => {
              // Prioritize same category
              const aScore =
                (a.category === currentBlog.category ? 1000 : 0) + b.createdAt;
              const bScore =
                (b.category === currentBlog.category ? 1000 : 0) + a.createdAt;
              return bScore - aScore;
            })
            .slice(0, 3);

          setRelatedBlogs(related);
        } else {
          // Blog not found, redirect to blogs page
          navigate("/blogs");
        }
      } catch (error) {
        console.error("Error fetching blog:", error);
        navigate("/blogs");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogAndRelated();
  }, [slug, navigate]);

  // Track blog view and time spent
  useEffect(() => {
    if (blog) {
      // Track blog view
      trackBlogView(blog.title, blog.category, blog.firestoreId, currentUser);

      // Track time spent on page when leaving
      const handleBeforeUnload = () => {
        const timeSpent = Math.floor(
          (Date.now() - startTimeRef.current) / 1000
        );
        if (timeSpent > 5) {
          // Only track if spent more than 5 seconds
          trackBlogReadTime(blog.title, timeSpent, currentUser);
        }
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
        // Track on unmount (e.g., navigating to another page)
        const timeSpent = Math.floor(
          (Date.now() - startTimeRef.current) / 1000
        );
        if (timeSpent > 5) {
          trackBlogReadTime(blog.title, timeSpent, currentUser);
        }
      };
    }
  }, [blog, currentUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFAF6] text-[#26313B] flex items-center justify-center">
        <motion.div
          className="w-12 h-12 border-3 border-[#E8B44C]/40 border-t-[#A8720E] rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  if (!blog) {
    return null;
  }

  // Extract plain text excerpt for meta description
  const extractPlainText = (markdown) => {
    const plainText = markdown
      .replace(/^#+ .+$/gm, "") // Remove headings
      .replace(/\*\*(.+?)\*\*/g, "$1") // Remove bold
      .replace(/\*(.+?)\*/g, "$1") // Remove italic
      .replace(/\[(.+?)\]\(.+?\)/g, "$1") // Remove links
      .replace(/`(.+?)`/g, "$1") // Remove inline code
      .replace(/^\s*[-*+]\s+/gm, "") // Remove list markers
      .trim();
    return plainText.substring(0, 160) + (plainText.length > 160 ? "..." : "");
  };

  const metaDescription = extractPlainText(blog.text);
  const currentUrl = `https://cosmovexapps.com/blog/${slug}`;
  const publishDate = new Date(blog.createdAt).toISOString();

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blog.title,
    description: metaDescription,
    author: {
      "@type": "Organization",
      name: "Cosmovex",
    },
    publisher: {
      "@type": "Organization",
      name: "Cosmovex",
      logo: {
        "@type": "ImageObject",
        url: "https://cosmovexapps.com/logo192.png",
      },
    },
    datePublished: publishDate,
    dateModified: publishDate,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": currentUrl,
    },
    articleSection: blog.category,
    keywords: `${blog.category}, technology, AI, development, programming`,
  };

  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#26313B] overflow-x-hidden w-full max-w-full">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{blog.title} | Cosmovex Tech Blog</title>
        <meta name="title" content={`${blog.title} | Cosmovex Tech Blog`} />
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={currentUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:site_name" content="Cosmovex" />
        <meta property="article:published_time" content={publishDate} />
        <meta property="article:section" content={blog.category} />
        <meta property="article:tag" content={blog.category} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={currentUrl} />
        <meta property="twitter:title" content={blog.title} />
        <meta property="twitter:description" content={metaDescription} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <GridBackground />
      <FloatingOrbs />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FBFAF6]/80 backdrop-blur-xl border-b border-[#E1DCCE]">
        <div className="max-w-5xl mx-auto px-2 md:px-6 py-2 md:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 md:gap-3 group">
            <div className="relative">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-[#E8B44C] border border-[#D9A32E] rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-[#E8B44C]/30 group-hover:shadow-[#E8B44C]/50 transition-shadow">
                <span className="text-base md:text-lg font-black text-[#221A06]">C</span>
              </div>
              <motion.div className="absolute -inset-1 bg-[#E8B44C] rounded-lg md:rounded-xl opacity-0 group-hover:opacity-30 blur-md transition-opacity" />
            </div>
            <div>
              <h1 className="text-base md:text-lg font-bold text-[#26313B] group-hover:text-[#A8720E] transition-colors">
                Cosmovex
              </h1>
              <p className="text-xs text-[#8A93A2] font-mono hidden md:block">// blog</p>
            </div>
          </Link>

          <nav className="flex items-center gap-1 md:gap-2">
            <Link
              to="/blogs"
              className="px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all"
            >
              All Blogs
            </Link>
            <Link
              to="/"
              className="px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all"
            >
              Home
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-2 md:px-6 py-4 md:py-12 relative z-10">
        <article>
          {/* Back button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-4 md:mb-8"
          >
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 text-sm text-[#5B6B7C] hover:text-[#0F6BBF] transition-colors group"
            >
              <svg
                className="w-4 h-4 group-hover:-translate-x-1 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to all blogs
            </Link>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-2xl md:text-5xl font-bold text-[#26313B] mb-4 md:mb-6 leading-tight"
          >
            {blog.title || "Untitled Post"}
          </motion.h1>

          {/* Category and Date */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="flex items-center gap-2 md:gap-3 mb-5 md:mb-8"
          >
            <CategoryBadge category={blog.category} />
            <span className="text-xs md:text-sm text-[#8A93A2] font-mono">
              {new Date(blog.createdAt).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </motion.div>

          {/* Blog Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -inset-[1px] bg-[#E8B44C]/10 rounded-xl md:rounded-3xl opacity-50 blur-sm" />
            <div className="relative bg-white shadow-[0_1px_2px_rgba(52,44,26,0.06),0_4px_14px_rgba(52,44,26,0.05)] rounded-xl md:rounded-3xl border border-[#E1DCCE] px-2 py-3 md:p-12">
              <MarkdownRenderer content={blog.text} />
            </div>
          </motion.div>

          {/* Install CTA — auto-rendered for any app-linked article (drives installs) */}
          {(() => {
            const ctaApp = blog.appId ? apps.find((a) => a.id === blog.appId) : null;
            if (!ctaApp || !ctaApp.playstore) return null;
            return (
              <motion.a
                href={ctaApp.playstore}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="mt-4 md:mt-8 block group"
                aria-label={`Install ${ctaApp.name} on Google Play`}
              >
                <div className="relative overflow-hidden rounded-xl md:rounded-3xl border border-[#E1DCCE] bg-white shadow-[0_1px_2px_rgba(52,44,26,0.06),0_4px_14px_rgba(52,44,26,0.05)] p-5 md:p-8">
                  <div className="flex items-center gap-4 md:gap-6">
                    {ctaApp.icon && (
                      <img
                        src={ctaApp.icon}
                        alt={ctaApp.name}
                        className="w-16 h-16 md:w-20 md:h-20 rounded-2xl border border-[#E1DCCE] shadow-lg flex-shrink-0"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-[#A8720E]">
                        Get the app
                      </div>
                      <h3 className="text-lg md:text-2xl font-extrabold text-[#26313B]">
                        {ctaApp.name}
                      </h3>
                      {ctaApp.tagline && (
                        <p className="text-sm text-[#5B6B7C] mt-0.5">{ctaApp.tagline}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-5 flex">
                    <span className="inline-flex items-center gap-2 rounded-full bg-[#E8B44C] border border-[#D9A32E] text-[#221A06] font-bold px-6 py-3 text-sm md:text-base shadow-lg group-hover:scale-[1.03] transition-transform">
                      <svg viewBox="0 0 512 512" className="w-5 h-5" aria-hidden="true">
                        <path fill="#221A06" d="M48 59.5v393c0 6.6 7.4 10.6 13 7l312-196.5c5.3-3.3 5.3-11 0-14.3L61 52.5c-5.6-3.6-13 .4-13 7z" />
                      </svg>
                      Install free on Google Play
                    </span>
                  </div>
                </div>
              </motion.a>
            );
          })()}

          {/* Social Sharing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-4 md:mt-8"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-3 md:p-6 bg-white shadow-[0_1px_2px_rgba(52,44,26,0.06),0_4px_14px_rgba(52,44,26,0.05)] rounded-xl md:rounded-2xl border border-[#E1DCCE]">
              <div>
                <h3 className="text-base md:text-lg font-semibold text-[#26313B] mb-1">
                  Enjoyed this article?
                </h3>
                <p className="text-xs md:text-sm text-[#5B6B7C]">
                  Share it with your network
                </p>
              </div>
              <div className="flex items-center flex-wrap gap-2">
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                    window.location.href
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackSocialShare("linkedin", blog.title, currentUser)
                  }
                  className="group flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-[#0077b5]/20 hover:bg-[#0077b5]/30 border border-[#0077b5]/30 hover:border-[#0077b5]/50 rounded-lg md:rounded-xl transition-all"
                  title="Share on LinkedIn"
                >
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5 text-[#0077b5]"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  <span className="text-xs md:text-sm font-medium text-[#26313B]">
                    LinkedIn
                  </span>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                    window.location.href
                  )}&text=${encodeURIComponent(blog.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackSocialShare("twitter", blog.title, currentUser)
                  }
                  className="group flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-[#1DA1F2]/20 hover:bg-[#1DA1F2]/30 border border-[#1DA1F2]/30 hover:border-[#1DA1F2]/50 rounded-lg md:rounded-xl transition-all"
                  title="Share on Twitter"
                >
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5 text-[#1DA1F2]"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                  <span className="text-xs md:text-sm font-medium text-[#26313B]">
                    Twitter
                  </span>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    window.location.href
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackSocialShare("facebook", blog.title, currentUser)
                  }
                  className="group flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-[#1877F2]/20 hover:bg-[#1877F2]/30 border border-[#1877F2]/30 hover:border-[#1877F2]/50 rounded-lg md:rounded-xl transition-all"
                  title="Share on Facebook"
                >
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5 text-[#1877F2]"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  <span className="text-xs md:text-sm font-medium text-[#26313B]">
                    Facebook
                  </span>
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    trackCopyLink(blog.title, currentUser);
                    alert("Link copied to clipboard!");
                  }}
                  className="group flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-[#F1EEE5] hover:bg-[#E1DCCE] border border-[#E1DCCE] hover:border-[#D9A32E] rounded-lg md:rounded-xl transition-all"
                  title="Copy link"
                >
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5 text-[#5B6B7C] group-hover:text-[#26313B] transition-colors"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                    />
                  </svg>
                  <span className="text-xs md:text-sm font-medium text-[#5B6B7C] group-hover:text-[#26313B] transition-colors">
                    Copy
                  </span>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Like & Comment Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mt-4 md:mt-8 space-y-3 md:space-y-6"
          >
            {/* Like Button */}
            <div className="flex items-center justify-center py-3 md:py-6 border-y border-[#E1DCCE]">
              <LikeButton
                targetType="blog"
                targetId={blog.firestoreId}
                initialLikesCount={blog.likesCount || 0}
              />
            </div>

            {/* Comment Section */}
            <CommentSection blogId={blog.firestoreId} blogTitle={blog.title} />
          </motion.div>
        </article>

        {/* Related Blogs */}
        {relatedBlogs.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-6 md:mt-16"
          >
            <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-8">
              <div className="h-px flex-1 bg-[#E1DCCE]" />
              <h2 className="text-lg md:text-2xl font-bold text-[#26313B]">
                More Articles
              </h2>
              <div className="h-px flex-1 bg-[#E1DCCE]" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              {relatedBlogs.map((relatedBlog) => (
                <RelatedBlogCard
                  key={relatedBlog.firestoreId}
                  blog={relatedBlog}
                  currentBlogTitle={blog.title}
                  user={currentUser}
                />
              ))}
            </div>
          </motion.section>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E1DCCE] py-4 md:py-8 mt-8 md:mt-20">
        <div className="max-w-5xl mx-auto px-2 md:px-6 text-center">
          <p className="text-[#8A93A2] text-xs md:text-sm">
            &copy; {new Date().getFullYear()} Cosmovex. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
